import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Manages the PWA install prompt banner.
// - Chrome/Edge (desktop/Android): captures beforeinstallprompt, shows banner with "Install" button
// - iOS (any browser): shows "Learn How" with Share → Add to Home Screen (same flow in Safari, Chrome, etc.)
// - Safari/macOS: shows "Learn How" with Add to Dock instructions
// - Already installed (standalone): never shows
// - Installed via browser (localStorage flag): shows "open from Home Screen/Dock" reminder
// - Dismissed: remembers in localStorage, never shows again
export default class extends Controller {
  static targets = [
    "banner",
    "installButton",
    "learnHowButton",
    "installedMessage",
    "installMessage",
    "safariInstructions",
    "safariMacInstructions"
  ]

  connect() {
    // Already running as installed PWA — don't show anything
    // If we haven't recorded the install yet, log it now (covers Safari "Add to Home Screen/Dock")
    if (this.isStandalone()) {
      if (!this.isInstalled()) {
        this.markInstalled()
        this.logInstall("standalone")
      }
      return
    }

    // Previously dismissed — don't show
    if (this.isDismissed()) return

    // Previously installed but browsing in browser — show "open from" reminder
    // (skip on macOS Safari since it has its own native "open in app" prompt)
    if (this.isInstalled() && !this.isSafariMac()) {
      this.showInstalledReminder()
      return
    }

    if (this.isIOS()) {
      // All iOS browsers (Safari, Chrome, Firefox, Edge) use the same Share → Add to Home Screen flow (iOS 16.4+).
      this.showForSafari("ios")
    } else if (this.isSafariMac()) {
      this.showForSafari("mac")
    } else {
      this.listenForInstallPrompt()
    }
  }

  disconnect() {
    if (this.boundBeforeInstallPrompt) {
      window.removeEventListener("beforeinstallprompt", this.boundBeforeInstallPrompt)
    }
  }

  // Chrome/Edge: listen for the beforeinstallprompt event
  listenForInstallPrompt() {
    this.deferredPrompt = null

    this.boundBeforeInstallPrompt = (event) => {
      event.preventDefault()
      this.deferredPrompt = event
      this.showBanner("install")
    }

    window.addEventListener("beforeinstallprompt", this.boundBeforeInstallPrompt)
  }

  // Show the banner and configure which button/message is visible
  showBanner(mode) {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.remove("hidden")
    }

    // Hide all mode-specific elements first
    if (this.hasInstallButtonTarget) this.installButtonTarget.classList.add("hidden")
    if (this.hasLearnHowButtonTarget) this.learnHowButtonTarget.classList.add("hidden")
    if (this.hasInstalledMessageTarget) this.installedMessageTarget.classList.add("hidden")
    if (this.hasInstallMessageTarget) this.installMessageTarget.classList.add("hidden")

    if (mode === "install") {
      if (this.hasInstallMessageTarget) this.installMessageTarget.classList.remove("hidden")
      if (this.hasInstallButtonTarget) this.installButtonTarget.classList.remove("hidden")
    } else if (mode === "learnHow") {
      if (this.hasInstallMessageTarget) this.installMessageTarget.classList.remove("hidden")
      if (this.hasLearnHowButtonTarget) this.learnHowButtonTarget.classList.remove("hidden")
    } else if (mode === "installed") {
      if (this.hasInstalledMessageTarget) this.installedMessageTarget.classList.remove("hidden")
    }
  }

  // Show reminder that the app is installed
  showInstalledReminder() {
    this.showBanner("installed")
  }

  // Safari: show with "Learn How" button
  showForSafari(platform) {
    this.safariPlatform = platform
    this.showBanner("learnHow")
  }

  // "Install" button clicked (Chrome/Edge)
  async install() {
    if (!this.deferredPrompt) return

    this.deferredPrompt.prompt()
    const { outcome } = await this.deferredPrompt.userChoice

    if (outcome === "accepted") {
      this.markInstalled()
      this.logInstall("chrome")
      this.hideBanner()
      document.dispatchEvent(new CustomEvent("banner:dismissed"))
    }

    this.deferredPrompt = null
  }

  // "Learn How" button clicked — toggle the appropriate instructions overlay
  toggleInstructions() {
    if (this.safariPlatform === "mac" && this.hasSafariMacInstructionsTarget) {
      this.safariMacInstructionsTarget.classList.toggle("hidden")
    } else if (this.safariPlatform === "ios" && this.hasSafariInstructionsTarget) {
      this.safariInstructionsTarget.classList.toggle("hidden")
    }
  }

  // Dismiss button (X) clicked
  dismiss() {
    this.markDismissed()
    this.hideBanner()

    // Let lower-priority banners (e.g. enable notifications) know they can show now
    document.dispatchEvent(new CustomEvent("banner:dismissed"))
  }

  hideBanner() {
    if (this.hasBannerTarget) {
      this.bannerTarget.classList.add("hidden")
    }
    if (this.hasSafariInstructionsTarget) {
      this.safariInstructionsTarget.classList.add("hidden")
    }
    if (this.hasSafariMacInstructionsTarget) {
      this.safariMacInstructionsTarget.classList.add("hidden")
    }
  }

  // Check if app is already running in standalone/PWA mode
  isStandalone() {
    return window.matchMedia("(display-mode: standalone)").matches ||
           window.navigator.standalone === true
  }

  // Check if user previously installed the PWA
  isInstalled() {
    try {
      return localStorage.getItem("pwa_installed") === "true"
    } catch {
      return false
    }
  }

  // Check if user previously dismissed the prompt
  isDismissed() {
    try {
      return localStorage.getItem("pwa_install_dismissed") === "true"
    } catch {
      return false
    }
  }

  // Remember that user installed the PWA
  markInstalled() {
    try {
      localStorage.setItem("pwa_installed", "true")
    } catch {
      // localStorage unavailable
    }
  }

  // Remember that user dismissed
  markDismissed() {
    try {
      localStorage.setItem("pwa_install_dismissed", "true")
    } catch {
      // localStorage unavailable — banner will show again next time
    }
  }

  // Log the install activity to the server
  async logInstall(method) {
    try {
      await post("/user_activities/activity", {
        body: JSON.stringify({
          event: "pwa.install",
          referrer: method
        })
      })
    } catch {
      // Best-effort — don't block the install flow
    }
  }

  // Detect Safari on iOS/iPadOS (no beforeinstallprompt support)
  isSafariIOS() {
    const ua = navigator.userAgent
    const isIOS = /iPad|iPhone|iPod/.test(ua) ||
                  (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS/.test(ua)
    return isIOS && isSafari
  }

  // Detect any iOS/iPadOS browser (Safari or otherwise).
  // Used to catch iOS Chrome/Firefox/Edge which don't fire beforeinstallprompt.
  isIOS() {
    const ua = navigator.userAgent
    return /iPad|iPhone|iPod/.test(ua) ||
           (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1)
  }

  // Detect Safari on macOS (supports "Add to Dock" since Sonoma).
  // Safe to call only after isSafariIOS() returns false (which handles iPads
  // that report "Macintosh" in their UA).
  isSafariMac() {
    const ua = navigator.userAgent
    const isMac = /Macintosh/.test(ua)
    const isSafari = /Safari/.test(ua) && !/Chrome|CriOS|FxiOS|EdgiOS|Chromium/.test(ua)
    return isMac && isSafari
  }
}
